import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import WarningLoading from './WarningLoading';
import RetryNotice from './RetryNotice';
import SetBy from './SetBy';
import apiRequest from '../../api/request';
import { WD, FONT_SERIF } from './warningTheme';

// "Who can set thresholds" - the second tab of Manage access. ONLY Finance gives or removes this (the screen is Finance-only and so is the server).
// Finance picks WHO - one or several people, a whole department, or a role (job title) under a department - and ticks the metrics whose threshold they
// may set; they then see the Configure threshold button on those metrics (and the gear on their cards) and manage the threshold from there, for every
// department. A department / role grant FOLLOWS the people: someone who joins the department or takes the role gets it, someone who leaves loses it.
// A person's access is everything they were given plus everything their roles and departments were given. Metrics that share one setup are ONE tick:
// the target slabs (TAR, ARPU, Number of Admissions, LCRR) and the department thresholds (ACL, CCR, FDR, SAR, ATP).
//   - ONE person / role / department: what is ticked becomes exactly what they have (the form shows what they have now); saving REPLACES it.
//   - SEVERAL: the ticked thresholds are ADDED to each (what they already have stays).
// Server side: warningDashboard/configAccess.js (the rule), configCatalogue.js (the setups), config_grants.controller.js and
// config_group_grants.controller.js (these endpoints).
const BASE = 'warning-dashboard/access/config-grants';
const failure = (res, fallback) => res?.message || fallback;
const heading = (text) => (
  <Typography component="h2" sx={{ fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 600, color: WD.ink, mb: 1.5 }}>{text}</Typography>
);

const EMPTY = []; // a stable empty list: a new [] on every render makes an Autocomplete think its value changed (and clear what is typed)
const keyOf = (type, id) => `${type}:${id}`;
const peopleWord = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;
const TYPE_LABEL = { user: 'Person', role: 'Role', department: 'Department' };
// the words for a department / role in chips and lists
export const granteeLabel = (g) => {
  if (g.type === 'department') return `${g.name} department`;
  if (g.type === 'role') return `${g.name}${g.department ? ` (${g.department})` : ''} role`;
  return g.name;
};

const ConfigGrantsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // the tab could not load
  const [actionError, setActionError] = useState(null); // a save failed
  const [notice, setNotice] = useState(null); // what the last save of several did
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null); // the grantee about to lose their access

  // the form: WHO (a mix of people, departments and roles) and WHICH thresholds
  const [mode, setMode] = useState('people'); // which picker is showing: people | department | role
  const [selection, setSelection] = useState([]); // [{ key, type, id, name, department?, people?, canSeePerformanceMetrics? }]
  const [ticked, setTicked] = useState([]); // the threshold keys ticked
  const [query, setQuery] = useState(''); // the people search
  const [options, setOptions] = useState([]);
  const [targets, setTargets] = useState(null); // { departments } - loaded the first time a department / role picker opens
  const [targetsError, setTargetsError] = useState(null);
  const targetsAsked = useRef(false); // the departments are asked for ONCE, even if the pickers are switched back and forth while the answer is on its way
  const [roleDept, setRoleDept] = useState(null); // the department whose roles are being picked
  const [roles, setRoles] = useState(null); // { departmentId, isFinance, roles } for roleDept

  const load = useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    // apiRequest resolves even on a failed call (src/api/request.js), so the status check is required
    return apiRequest({ url: BASE, method: 'get' })
      .then((res) => {
        if (res?.status !== 'success') { setError(failure(res, 'Could not load who can set thresholds')); return; }
        setData(res.data);
        setError(null);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Could not load who can set thresholds'))
      .finally(() => { if (!quiet) setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setOptions([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      apiRequest({ url: `${BASE}/people?search=${encodeURIComponent(text)}`, method: 'get' })
        .then((res) => { if (!cancelled && res?.status === 'success') setOptions(res.data?.people || []); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  // the department list for the department / role pickers - once
  useEffect(() => {
    if (mode === 'people' || targetsAsked.current) return;
    targetsAsked.current = true;
    apiRequest({ url: `${BASE}/targets`, method: 'get' })
      .then((res) => {
        if (res?.status !== 'success') { setTargetsError(failure(res, 'Could not load the departments')); return; }
        setTargets({ departments: res.data?.departments || [] });
      })
      .catch((err) => setTargetsError(err?.response?.data?.message || 'Could not load the departments'));
  }, [mode]);

  // the roles under the chosen department
  useEffect(() => {
    if (!roleDept) { setRoles(null); return undefined; }
    let cancelled = false;
    setRoles(null);
    const failed = (message) => ({ departmentId: roleDept.id, isFinance: false, roles: [], failed: message });
    apiRequest({ url: `${BASE}/targets/roles?departmentId=${roleDept.id}`, method: 'get' })
      .then((res) => { if (!cancelled) setRoles(res?.status === 'success' ? res.data : failed(failure(res, 'Could not load the roles'))); })
      .catch(() => { if (!cancelled) setRoles(failed('Could not load the roles')); });
    return () => { cancelled = true; };
  }, [roleDept]);

  const setups = data?.setups || [];
  const people = data?.people || [];
  const groups = data?.groups || [];
  const locked = !!data?.tableMissing;
  const setupName = (key) => setups.find((s) => s.key === key)?.name || key;

  // what a person / department / role was given directly
  const existingOf = (g) => (g.type === 'user' ? people.find((p) => p.userId === g.id) : groups.find((x) => x.type === g.type && x.id === g.id));
  const single = selection.length === 1 ? selection[0] : null;
  const existing = single ? existingOf(single) : null;
  const editing = !!existing;

  const resetForm = () => { setSelection([]); setTicked([]); setQuery(''); setOptions([]); setRoleDept(null); };
  const choose = (next) => {
    const wasEditing = selection.length === 1 && !!existingOf(selection[0]);
    setSelection(next);
    if (next.length === 1) { const ex = existingOf(next[0]); setTicked(ex ? ex.keys : (selection.length === 0 ? [] : ticked)); }
    else if (next.length === 0) setTicked([]);
    else if (wasEditing) setTicked([]); // several now: do not carry one grantee's existing access over to the others
  };
  const toUser = (p) => ({ ...p, key: keyOf('user', p.userId), type: 'user', id: p.userId });
  // stable between renders unless the selection really changes (see EMPTY)
  const selectedUsers = useMemo(() => selection.filter((g) => g.type === 'user'), [selection]);
  const selectedDepartments = useMemo(() => selection.filter((g) => g.type === 'department'), [selection]);
  const selectedRoles = useMemo(() => selection.filter((g) => g.type === 'role'), [selection]);
  const swapType = (type, picked) => choose([...selection.filter((g) => g.type !== type), ...picked]);
  const dropOne = (g) => choose(selection.filter((x) => x.key !== g.key));
  const toggle = (key) => setTicked((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  // Change on a list row: that one grantee, with what they have
  const fillFrom = (g) => {
    const grantee = g.type === 'user'
      ? toUser({ userId: g.userId, name: g.name, canSeePerformanceMetrics: g.canSeePerformanceMetrics })
      : { key: keyOf(g.type, g.id), type: g.type, id: g.id, name: g.name, department: g.department, people: g.peopleCount };
    setSelection([grantee]);
    setTicked(g.keys);
  };

  const wantedKeys = setups.map((x) => x.key).filter((k) => ticked.includes(k));
  const accepted = (res, fallback) => {
    if (res?.status !== 'success') { setActionError(failure(res, fallback)); return false; }
    return true;
  };
  const save = () => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    let request;
    if (single) {
      // ONE grantee: what is ticked becomes exactly what they have (a new one has nothing to replace)
      const url = single.type === 'user' ? `${BASE}/${single.id}` : `${BASE}/group/${single.type}/${single.id}`;
      request = apiRequest({ url, method: 'put', data: { keys: wantedKeys } });
    } else {
      request = apiRequest({ url: `${BASE}/add`, method: 'post', data: { grantees: selection.map((g) => ({ type: g.type, id: g.id })), keys: wantedKeys } });
    }
    return request
      .then((res) => {
        if (!accepted(res, 'Could not save')) return undefined;
        if (!single && res.data) setNotice(`Access given to ${res.data.changed} of ${selection.length}${res.data.unchanged ? ` (${res.data.unchanged} already had these)` : ''}.`);
        resetForm();
        return load(true);
      })
      .catch((err) => setActionError(err?.response?.data?.message || 'Could not save'))
      .finally(() => setBusy(false));
  };
  const remove = (who) => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    const url = who.type === 'user' ? `${BASE}/${who.userId}` : `${BASE}/group/${who.type}/${who.id}`;
    return apiRequest({ url, method: 'delete' })
      .then((res) => {
        if (!accepted(res, 'Could not remove')) return undefined;
        if (selection.length === 1 && selection[0].key === keyOf(who.type, who.id)) resetForm();
        return load(true);
      })
      .catch((err) => setActionError(err?.response?.data?.message || 'Could not remove'))
      .finally(() => setBusy(false));
  };

  const canSave = selection.length > 0 && !locked && !busy && ticked.length > 0;
  const blind = selection.filter((g) => g.type === 'user' && g.canSeePerformanceMetrics === false);
  const giveLabel = editing ? 'Save changes' : selection.length > 1 ? `Give access to ${selection.length}` : 'Give access';
  const withCount = (g) => (g.peopleCount !== undefined ? ` · ${peopleWord(g.peopleCount)}` : '');
  const removingPerson = !removing || removing.type === 'user';

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
        Finance can set every threshold. Here you decide who else can: pick one or several people, a whole department, or a role (job title) under
        a department, and tick the metrics whose threshold they may set. They then see the Configure threshold button on those metrics and manage the
        threshold from there. A department or role keeps working as people join and leave it. Metrics that share one setup are set together -
        TAR, ARPU, Number of Admissions and LCRR share the target slabs; ACL, CCR, FDR, SAR and ATP are set together. Only Finance can give or
        remove this access.
      </Typography>

      {loading && <WarningLoading />}
      {!loading && error && <RetryNotice message={error} onRetry={() => load()} />}
      {actionError && <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>{actionError}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
      {!loading && !error && locked && <Alert severity="warning" sx={{ mb: 2 }}>{data.tableMissingMessage}</Alert>}

      {!loading && !error && data && (
        <>
          {heading('Who can set each threshold')}
          <TableContainer sx={{ maxWidth: 1000, mb: 5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                  <TableCell><strong>Threshold</strong></TableCell>
                  <TableCell><strong>Metrics it covers</strong></TableCell>
                  <TableCell><strong>Who can set it</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {setups.map((s) => (
                  <TableRow key={s.key} hover>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      {s.metrics.join(' · ')}
                      {s.metrics.length > 1 && <Typography variant="caption" color="text.secondary" display="block">set together</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Chip size="small" label="Finance" />
                        {s.people.map((p) => <Chip key={p.userId} size="small" variant="outlined" label={p.name} />)}
                        {(s.groups || []).map((g) => (
                          <Chip key={keyOf(g.type, g.id)} size="small" variant="outlined" color="secondary" label={`${TYPE_LABEL[g.type]}: ${g.type === 'role' && g.department ? `${g.name} (${g.department})` : g.name}${withCount(g)}`} />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {heading('Give access')}
          <Box display="flex" flexDirection="column" gap={2} sx={{ maxWidth: 760, mb: 5 }}>
            <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, next) => { if (next) setMode(next); }} aria-label="Give access to">
              <ToggleButton value="people" sx={{ textTransform: 'none', px: 2 }}>People</ToggleButton>
              <ToggleButton value="department" sx={{ textTransform: 'none', px: 2 }}>A department</ToggleButton>
              <ToggleButton value="role" sx={{ textTransform: 'none', px: 2 }}>A role in a department</ToggleButton>
            </ToggleButtonGroup>

            {mode === 'people' && (
              <Autocomplete
                multiple
                size="small"
                sx={{ maxWidth: 420 }}
                options={options}
                value={selectedUsers}
                disabled={locked}
                onChange={(e, v) => swapType('user', v.map((p) => (p.type === 'user' ? p : toUser(p))))}
                onInputChange={(e, v, reason) => setQuery(reason === 'reset' ? '' : v)}
                inputValue={query}
                filterOptions={(x) => x}
                renderTags={() => null}
                getOptionLabel={(p) => p.name}
                getOptionDisabled={(p) => !!p.isFinance}
                isOptionEqualToValue={(a, b) => (a.userId ?? a.id) === (b.userId ?? b.id)}
                noOptionsText={query.trim().length < 2 ? 'Type at least 2 letters' : 'No one found'}
                renderOption={(props, p) => (
                  <li {...props} key={p.userId}>
                    <Box>
                      <Typography variant="body2">{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.departments.join(', ') || 'No department'}
                        {p.isFinance ? ' · in Finance, which already has full access' : p.alreadyGranted ? ' · already has access (saving replaces it)' : ''}
                        {!p.isFinance && (p.inherited || []).length > 0 ? ` · also through ${p.inherited.map(granteeLabel).join(', ')}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Person" />}
              />
            )}

            {mode !== 'people' && targetsError && <Alert severity="error">{targetsError}</Alert>}
            {mode === 'department' && !targetsError && (
              <Autocomplete
                multiple
                size="small"
                sx={{ maxWidth: 420 }}
                options={targets?.departments || EMPTY}
                loading={!targets}
                value={selectedDepartments}
                disabled={locked}
                onChange={(e, v) => swapType('department', v.map((d) => (d.type === 'department' ? d : { key: keyOf('department', d.id), type: 'department', id: d.id, name: d.name, people: d.people })))}
                renderTags={() => null}
                getOptionLabel={(d) => d.name}
                getOptionDisabled={(d) => !!d.isFinance}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="No department found"
                renderOption={(props, d) => (
                  <li {...props} key={d.id}>
                    <Box>
                      <Typography variant="body2">{d.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {peopleWord(d.people)} · {d.roles} {d.roles === 1 ? 'role' : 'roles'}
                        {d.isFinance ? ' · Finance already has full access' : d.hasAccess ? ' · already has access (saving replaces it)' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Department" />}
              />
            )}

            {mode === 'role' && !targetsError && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Autocomplete
                  size="small"
                  sx={{ maxWidth: 420 }}
                  options={targets?.departments || EMPTY}
                  loading={!targets}
                  value={roleDept}
                  disabled={locked}
                  onChange={(e, v) => setRoleDept(v)}
                  getOptionLabel={(d) => d.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  noOptionsText="No department found"
                  renderOption={(props, d) => (
                    <li {...props} key={d.id}>
                      <Box>
                        <Typography variant="body2">{d.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{peopleWord(d.people)} · {d.roles} {d.roles === 1 ? 'role' : 'roles'}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="The role is in department" />}
                />
                {roleDept && roles?.isFinance && <Alert severity="info">Finance already has full access, so there is nothing to give to its roles.</Alert>}
                {roleDept && roles?.failed && <Alert severity="error">{roles.failed}</Alert>}
                {roleDept && !roles?.isFinance && !roles?.failed && (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ maxWidth: 420 }}
                    options={roles?.roles || EMPTY}
                    loading={!roles}
                    value={selectedRoles}
                    disabled={locked}
                    onChange={(e, v) => swapType('role', v.map((r) => (r.type === 'role' ? r : { key: keyOf('role', r.id), type: 'role', id: r.id, name: r.name, department: roleDept.name, people: r.people })))}
                    renderTags={() => null}
                    getOptionLabel={(r) => r.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    noOptionsText="No role found"
                    renderOption={(props, r) => (
                      <li {...props} key={r.id}>
                        <Box>
                          <Typography variant="body2">{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{peopleWord(r.people)}{r.hasAccess ? ' · already has access (saving replaces it)' : ''}</Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Role" />}
                  />
                )}
              </Box>
            )}

            {selection.length > 0 && (
              <>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Giving access to</Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {selection.map((g) => (
                      <Chip
                        key={g.key}
                        size="small"
                        color={g.type === 'user' ? 'default' : 'secondary'}
                        label={g.type === 'user' ? g.name : `${granteeLabel(g)}${g.people !== undefined ? ` · ${peopleWord(g.people)}` : ''}`}
                        onDelete={() => dropOne(g)}
                      />
                    ))}
                  </Box>
                </Box>
                {blind.length > 0 && (
                  <Alert severity="warning">
                    {blind.length === 1
                      ? `${blind[0].name}'s department cannot see Performance Metrics at the moment, so they will not see the Configure threshold button on the metrics.`
                      : `${blind.map((b) => b.name).join(', ')} cannot see Performance Metrics at the moment (their departments are switched off), so they will not see the Configure threshold button on the metrics.`}
                    {' '}Add them as an exception on the first tab.
                  </Alert>
                )}
                <Box>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {single ? `${single.type === 'user' ? single.name : granteeLabel(single)} can set the threshold for` : `The ${selection.length} selected can set the threshold for`}
                    </Typography>
                    <Button size="small" onClick={() => setTicked(setups.map((s) => s.key))}>Select all</Button>
                    <Button size="small" onClick={() => setTicked([])}>Clear</Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {editing ? 'Saving replaces what they have now.' : selection.length > 1 ? 'These are ADDED for each of them - whatever they already have stays.' : 'Nothing is set for them yet.'}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
                    {setups.map((s) => (
                      <FormControlLabel
                        key={s.key}
                        control={<Checkbox size="small" checked={ticked.includes(s.key)} onChange={() => toggle(s.key)} />}
                        label={(
                          <Box>
                            <Typography variant="body2">{s.name}</Typography>
                            {s.metrics.length > 1 && <Typography variant="caption" color="text.secondary">{s.metrics.join(' · ')} (set together)</Typography>}
                          </Box>
                        )}
                        sx={{ alignItems: 'flex-start', mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            <Box display="flex" gap={1.5}>
              <Button variant="contained" disabled={!canSave} onClick={save}>{giveLabel}</Button>
              {selection.length > 0 && <Button variant="text" onClick={resetForm}>Cancel</Button>}
            </Box>
          </Box>

          {(people.length > 0 || groups.length === 0) && heading('People who can set thresholds')}
          {people.length === 0 && groups.length === 0 && <Typography variant="body2" color="text.secondary">Nobody besides Finance yet.</Typography>}
          {people.length > 0 && (
            <TableContainer sx={{ maxWidth: 1100, mb: groups.length ? 5 : 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                    <TableCell><strong>Person</strong></TableCell>
                    <TableCell><strong>Their department</strong></TableCell>
                    <TableCell><strong>Can set</strong></TableCell>
                    <TableCell><strong>Given</strong></TableCell>
                    <TableCell align="right"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {people.map((p) => (
                    <TableRow key={p.userId} hover>
                      <TableCell>
                        {p.name}{!p.active && ' (inactive)'}
                        {p.canSeePerformanceMetrics === false && (
                          <Typography variant="caption" color="warning.main" display="block">Cannot see Performance Metrics</Typography>
                        )}
                      </TableCell>
                      <TableCell>{p.departmentsOfPerson.join(', ') || '-'}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {p.keys.map((k) => <Chip key={k} size="small" variant="outlined" label={setupName(k)} />)}
                        </Box>
                        {(p.inherited || []).length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Also through: {p.inherited.map((g) => `${granteeLabel(g)} (${g.keys.map(setupName).join(', ')})`).join('; ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell><SetBy name={p.grantedBy} at={p.grantedAt} label="Given by" /></TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" disabled={locked || busy} onClick={() => fillFrom({ type: 'user', userId: p.userId, id: p.userId, name: p.name, keys: p.keys, canSeePerformanceMetrics: p.canSeePerformanceMetrics })}>Change</Button>
                          <Button size="small" variant="outlined" color="error" disabled={locked || busy} onClick={() => setRemoving({ type: 'user', userId: p.userId, id: p.userId, name: p.name })}>Remove</Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {groups.length > 0 && (
            <>
              {heading('Departments and roles who can set thresholds')}
              <TableContainer sx={{ maxWidth: 1100 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                      <TableCell><strong>Department / role</strong></TableCell>
                      <TableCell align="right"><strong>People</strong></TableCell>
                      <TableCell><strong>Can set</strong></TableCell>
                      <TableCell><strong>Given</strong></TableCell>
                      <TableCell align="right"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((g) => (
                      <TableRow key={keyOf(g.type, g.id)} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Chip size="small" color="secondary" variant="outlined" label={TYPE_LABEL[g.type]} />
                            <span>{g.name}</span>
                          </Box>
                          {g.type === 'role' && g.department && <Typography variant="caption" color="text.secondary" display="block">{g.department}</Typography>}
                          {g.exists === false && <Typography variant="caption" color="warning.main" display="block">No longer exists - nobody gets this; you can remove it</Typography>}
                        </TableCell>
                        <TableCell align="right">{g.peopleCount}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {g.keys.map((k) => <Chip key={k} size="small" variant="outlined" label={setupName(k)} />)}
                          </Box>
                        </TableCell>
                        <TableCell><SetBy name={g.grantedBy} at={g.grantedAt} label="Given by" /></TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Button size="small" variant="outlined" disabled={locked || busy} onClick={() => fillFrom(g)}>Change</Button>
                            <Button size="small" variant="outlined" color="error" disabled={locked || busy} onClick={() => setRemoving(g)}>Remove</Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      <Dialog open={!!removing} onClose={() => setRemoving(null)}>
        <DialogTitle>{removingPerson ? `Remove ${removing?.name}'s access?` : `Remove the access of ${granteeLabel(removing)}?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {removingPerson
              ? `${removing?.name} will no longer be able to set any threshold. Thresholds they already set stay as they are, and you can give them access again at any time.`
              : `Everyone in ${granteeLabel(removing)} will lose what they only had through it${removing.peopleCount ? ` (${peopleWord(removing.peopleCount)})` : ''}. Anyone who was also given access in their own name, or through another role or department, keeps that. Thresholds already set stay as they are, and you can give access again at any time.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => { const who = removing; setRemoving(null); remove(who); }}>Remove access</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfigGrantsPanel;
